import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtINR } from "../lib/constants";
import {
  Briefcase, FileText, Receipt, CheckCircle2, LogOut, Upload, AlertCircle,
  Download, MessageSquare, Calendar, CheckCircle, Clock, X, FileUp, ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [settings, setSettings] = useState({});
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [pending, setPending] = useState([]);
  const [charter, setCharter] = useState(null);
  const [approving, setApproving] = useState(false);
  const [drillKind, setDrillKind] = useState(null); // 'overdue' | 'pending-docs' | null

  useEffect(() => {
    (async () => {
      const [p, s, pe, inv] = await Promise.all([
        api.get("/projects"),
        api.get("/settings/company"),
        api.get("/client-pending"),
        api.get("/invoices"),
      ]);
      setProjects(p.data);
      setSettings(s.data || {});
      setPending(pe.data);
      setInvoices(inv.data || []);
      if (p.data.length > 0) setActiveId(p.data[0].id);
    })();
  }, []);

  const reloadProject = async (pid) => {
    if (!pid) return;
    const [d, c, ms, rem, ch] = await Promise.all([
      api.get(`/projects/${pid}/documents`),
      api.get(`/compliance`, { params: { project_id: pid } }),
      api.get(`/projects/${pid}/milestones`).catch(() => ({ data: [] })),
      api.get(`/projects/${pid}/remarks`).catch(() => ({ data: [] })),
      api.get(`/projects/${pid}/charter`).catch(() => ({ data: null })),
    ]);
    setDocs(d.data); setCompliance(c.data);
    setMilestones(ms.data || []); setRemarks(rem.data || []);
    setCharter(ch.data || null);
  };
  useEffect(() => { reloadProject(activeId); }, [activeId]);

  const active = useMemo(() => projects.find(p => p.id === activeId), [projects, activeId]);
  const awaitingItems = useMemo(() => pending.filter(p => p.status === "Pending"), [pending]);
  const upcomingMilestones = useMemo(() => milestones.filter(m => m.status !== "Completed").slice(0, 8), [milestones]);

  const today = new Date().toISOString().slice(0, 10);
  const dayDiff = (d) => d ? Math.ceil((new Date(d) - new Date(today)) / 86400000) : 0;

  // Compute KPIs across ALL projects for header summary
  const allOverdue = compliance.filter(c => c.status !== "Closed" && dayDiff(c.due_date) < 0);
  const overdueDocs = docs.filter(d => d.status === "Pending");
  const docProgress = docs.length ? Math.round(docs.filter(d => d.status === "Approved").length / docs.length * 100) : 0;
  const projInvoices = invoices.filter(i => active && i.project_id === active.id);
  const outstanding = projInvoices.reduce((a, i) => a + Math.max(0, (i.amount || 0) - (i.paid_amount || 0)), 0);

  const approveCharter = async () => {
    setApproving(true);
    try {
      await api.post(`/client/charter/${activeId}/approve`);
      toast.success("Charter approved — thank you!");
      reloadProject(activeId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
    finally { setApproving(false); }
  };

  const markProvided = async (id) => {
    try { await api.put(`/client-pending/${id}`, { status: "Provided" });
      toast.success("Marked as provided");
      const r = await api.get("/client-pending"); setPending(r.data);
    } catch { toast.error("Failed"); }
  };

  const uploadDocFile = async (docId, file, projectId) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8 MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = reader.result;
        if (docId) {
          await api.put(`/client/documents/${docId}/upload`, { name: file.name, file_url: dataUrl, file_size: file.size, project_id: projectId });
        } else {
          await api.post("/client/documents/upload", { name: file.name, file_url: dataUrl, file_size: file.size, project_id: projectId });
        }
        toast.success(`${file.name} uploaded`);
        reloadProject(projectId);
      } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" style={{ fontFamily: "Poppins, sans-serif" }}>
      <header className="text-white px-4 md:px-8 py-4 flex items-center justify-between" style={{ background: "#0F2A4A" }}>
        <div className="flex items-center gap-3">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt="PT" className="w-10 h-10 rounded-lg object-cover bg-white/10 p-1" />
          ) : (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg" style={{ background: "#72BE44" }}>PT</div>
          )}
          <div>
            <div className="font-bold tracking-tight text-base">PT — ECMP Platform</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300">{user?.name}</div>
          </div>
        </div>
        <button onClick={async () => { await logout(); nav("/login"); }} data-testid="client-logout" className="text-xs flex items-center gap-2 text-slate-300 hover:text-white">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </header>

      <div className="p-4 md:p-8 max-w-7xl mx-auto w-full space-y-6 animate-fade-up flex-1">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] font-semibold mb-2">Welcome back</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F2A4A]">{user?.name}</h1>
          <p className="text-stone-500 mt-2 text-sm">{projects.length} project{projects.length !== 1 ? "s" : ""} · Track milestones, upload documents, and follow PT team updates.</p>
        </div>

        {/* MULTI-PROJECT CARD GRID */}
        {projects.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="client-projects-grid">
            {projects.map(p => (
              <button key={p.id} onClick={() => setActiveId(p.id)} data-testid={`client-project-card-${p.id}`}
                className={`text-left p-4 rounded-xl border-2 transition ${activeId === p.id ? "border-[#72BE44] bg-[#72BE44]/5" : "border-stone-200 bg-white hover:border-[#72BE44]/40"}`}>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{p.project_id_external || p.id.slice(0, 8)}</div>
                <div className="font-bold text-[#0F2A4A] mt-1">{p.project_name}</div>
                <div className="text-xs text-stone-600 mt-1">{p.service_type} · {p.project_location || "—"}</div>
                <div className="flex items-center gap-2 mt-3 text-[10px]">
                  <span className="px-2 py-0.5 bg-stone-100 rounded-full text-stone-700 font-medium">{p.current_stage || "Active"}</span>
                  <span className="text-stone-400">·</span>
                  <span className="text-[#0F2A4A] font-semibold">{fmtINR(p.contract_value)}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {active && (
          <>
            {/* SELECTED PROJECT HEADER */}
            <div className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#72BE44] font-bold">{active.service_type}</div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#0F2A4A] mt-1">{active.project_name}</h2>
                  <p className="text-stone-500 text-sm mt-1">{active.project_location} · ID {active.project_id_external || active.id.slice(0, 8)}</p>
                </div>
                <a href={`${API}/client/snapshot/${active.id}/pdf`} target="_blank" rel="noreferrer" data-testid="snapshot-pdf"
                   className="flex items-center gap-2 bg-[#72BE44] text-white px-3 py-2 rounded text-xs font-semibold">
                  <Download className="w-4 h-4" /> Snapshot PDF
                </a>
              </div>

              {/* JOURNEY STEPPER */}
              <div className="mt-5 relative overflow-x-auto pb-2">
                <div className="absolute top-3 left-3 right-3 h-0.5 bg-stone-200" />
                {(() => {
                  const stages = active.stages || [];
                  const idx = Math.max(stages.indexOf(active.current_stage), 0);
                  return (
                    <>
                      <div className="absolute top-3 left-3 h-0.5" style={{ background: "#72BE44", width: `calc(${(idx / Math.max(stages.length - 1, 1)) * 100}% - 12px)` }} />
                      <div className="relative flex justify-between min-w-[600px]">
                        {stages.map((s, i) => (
                          <div key={s} className="flex flex-col items-center gap-2 flex-1">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center border-2"
                                 style={{ background: i <= idx ? "#72BE44" : "white", borderColor: i <= idx ? "#72BE44" : "#d6d3d1", color: "white" }}>
                              {i <= idx && <CheckCircle2 className="w-3.5 h-3.5" />}
                            </div>
                            <div className={`text-[10px] text-center px-1 ${i <= idx ? "text-[#0F2A4A] font-semibold" : "text-stone-500"}`}>{s}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* CLICKABLE KPIs (with drill-down) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <KPI icon={Briefcase} label="Project Value" value={fmtINR(active.contract_value)} color="#0F2A4A" />
              <KPI icon={FileText} label="Documents" value={`${docProgress}%`} color="#72BE44"
                   sub={`${docs.filter(d => d.status === "Approved").length}/${docs.length} approved`} />
              <ClickableKPI icon={AlertCircle} label="Pending Docs" value={overdueDocs.length} color="#D97706" testid="kpi-pending-docs"
                            onClick={() => setDrillKind("pending-docs")} />
              <ClickableKPI icon={AlertCircle} label="Overdue Compliance" value={allOverdue.length} color="#B91C1C" testid="kpi-overdue"
                            onClick={() => setDrillKind("overdue")} />
            </div>

            {/* AWAITING FROM CLIENT */}
            {awaitingItems.length > 0 && (
              <div className="bg-white border-2 border-red-200 rounded-xl p-5" data-testid="client-pending-section">
                <div className="text-[11px] uppercase tracking-[0.22em] text-red-700 font-bold mb-3">Awaiting from your team — {awaitingItems.length} item(s)</div>
                <div className="space-y-2">
                  {awaitingItems.map(p => (
                    <div key={p.id} className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 mt-0.5 text-red-700 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-stone-900">{p.description}</div>
                        <div className="text-xs text-stone-500 mt-0.5">Requested: {p.date_requested} · <b className="text-red-700">{p.days_waiting} days waiting</b></div>
                      </div>
                      <button onClick={() => markProvided(p.id)} data-testid={`mark-provided-${p.id}`}
                        className="text-xs flex items-center gap-1.5 bg-[#72BE44] text-white px-3 py-1.5 rounded-md font-medium">
                        <Upload className="w-3 h-3" /> Mark Provided
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CHARTER SIGN-OFF */}
            {charter && (
              <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="client-charter-section">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#72BE44]" />
                      <div className="text-base font-semibold text-[#0F2A4A]">Project Charter</div>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                      {charter.approval_status === "Approved"
                        ? `Approved${charter.approved_by_name ? ` by ${charter.approved_by_name}` : ""} on ${charter.approved_at ? new Date(charter.approved_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}`
                        : "Please review the project scope & timeline with your PT team, then approve the charter."}
                    </div>
                  </div>
                  {charter.approval_status === "Approved" ? (
                    <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-full" data-testid="client-charter-approved">
                      <CheckCircle2 className="w-4 h-4" /> Charter Approved
                    </span>
                  ) : (
                    <button onClick={approveCharter} disabled={approving} data-testid="client-charter-approve-btn"
                      className="flex items-center gap-1.5 bg-[#0F2A4A] hover:bg-[#1a3a5f] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
                      <CheckCircle2 className="w-4 h-4" /> {approving ? "Approving..." : "Approve Charter"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* LATEST FROM PT TEAM (Remarks) */}
            <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="pt-remarks-section">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-[#72BE44]" />
                <div className="text-base font-semibold text-[#0F2A4A]">Latest from PT Team</div>
              </div>
              {remarks.length === 0 ? (
                <div className="text-sm text-stone-500">No updates yet from your project team.</div>
              ) : (
                <div className="space-y-3">
                  {remarks.slice(0, 5).map(r => (
                    <div key={r.id} data-testid={`remark-${r.id}`} className="flex gap-3 border-l-2 border-[#72BE44] pl-3 py-1">
                      <div className="flex-1">
                        <div className="text-sm text-stone-800 whitespace-pre-wrap">{r.text}</div>
                        <div className="text-[10px] text-stone-500 mt-1 uppercase tracking-wider">
                          {r.author_name || "PT Team"} · {(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.type || "Update"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* DOCUMENTS — with per-row upload */}
            <div className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-base font-semibold text-[#0F2A4A]">Project Documents ({docs.length})</div>
                <label className="text-xs flex items-center gap-1.5 bg-[#0F2A4A] text-white px-3 py-1.5 rounded cursor-pointer font-medium" data-testid="upload-new-doc">
                  <FileUp className="w-3.5 h-3.5" /> Upload New
                  <input type="file" hidden onChange={(e) => uploadDocFile(null, e.target.files[0], active.id)} />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {docs.map(d => (
                  <div key={d.id} className="flex items-center gap-3 border border-stone-100 rounded-lg p-3">
                    <div className={`w-2 h-2 rounded-full ${d.status === "Approved" ? "bg-[#72BE44]" : d.status === "Uploaded" ? "bg-sky-500" : "bg-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-800 truncate">{d.name}</div>
                      {d.source === "client" && <div className="text-[10px] text-[#72BE44] font-medium">Uploaded by you</div>}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 px-2 py-0.5 bg-stone-50 rounded">{d.status}</span>
                    {d.status === "Pending" && (
                      <label data-testid={`upload-doc-${d.id}`} className="cursor-pointer text-xs flex items-center gap-1 text-[#72BE44] hover:text-[#5da636] font-semibold">
                        <Upload className="w-3.5 h-3.5" /> Upload
                        <input type="file" hidden onChange={(e) => uploadDocFile(d.id, e.target.files[0], active.id)} />
                      </label>
                    )}
                    {d.file_url && d.status !== "Pending" && (
                      <a href={d.file_url} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-[#0F2A4A]"><Download className="w-3.5 h-3.5" /></a>
                    )}
                  </div>
                ))}
                {docs.length === 0 && <div className="text-sm text-stone-500 col-span-full py-4">No documents yet for this project.</div>}
              </div>
            </div>

            {/* MILESTONES (client_visible only) + Compliance + Invoices */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-stone-200 rounded-xl p-5">
                <div className="text-base font-semibold text-[#0F2A4A] mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#72BE44]" /> Upcoming Milestones</div>
                <div className="space-y-2">
                  {upcomingMilestones.map(m => (
                    <div key={m.id} data-testid={`client-ms-${m.id}`} className="flex items-center justify-between border-b border-stone-100 pb-2 last:border-b-0">
                      <div>
                        <div className="text-sm text-[#0F2A4A] font-medium">{m.name}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-wider">{m.phase}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-stone-700">{m.target_date}</div>
                        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${
                          m.status === "Delayed" ? "bg-rose-100 text-rose-800" :
                          m.status === "In Progress" ? "bg-amber-100 text-amber-800" :
                          "bg-stone-100 text-stone-700"
                        }`}>{m.status}</span>
                      </div>
                    </div>
                  ))}
                  {milestones.length === 0 && <div className="text-sm text-stone-500">No timeline yet for this project.</div>}
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-xl p-5">
                <div className="text-base font-semibold text-[#0F2A4A] mb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#72BE44]" /> Invoices</div>
                <div className="space-y-2">
                  {projInvoices.map(i => (
                    <div key={i.id} className="flex items-center justify-between border-b border-stone-100 pb-2 last:border-b-0">
                      <div>
                        <div className="font-mono text-xs text-stone-700">{i.invoice_number}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-wider">Due {i.due_date}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-[#0F2A4A]">{fmtINR(i.amount)}</div>
                        <span className="text-[10px] uppercase tracking-wider text-stone-500">{i.status}</span>
                      </div>
                    </div>
                  ))}
                  {projInvoices.length === 0 && <div className="text-sm text-stone-500">No invoices yet.</div>}
                  {outstanding > 0 && <div className="text-xs mt-2 px-3 py-2 bg-amber-50 text-amber-800 rounded-lg font-medium">Outstanding: {fmtINR(outstanding)}</div>}
                </div>
              </div>
            </div>
          </>
        )}
        {projects.length === 0 && <div className="bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-500">No projects assigned yet. Your PT team will set up your project shortly.</div>}
      </div>

      {drillKind && (
        <DrillModal kind={drillKind} onClose={() => setDrillKind(null)}
                    overdueItems={allOverdue} pendingDocs={overdueDocs}
                    activeProjectId={active?.id} onUpload={uploadDocFile} />
      )}

      <footer className="px-4 md:px-8 py-3 text-xs text-white text-center" style={{ background: "#72BE44" }}>
        <b>PT</b> — Your Environmental Compliance Managing Partner
      </footer>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 md:p-5 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{label}</span>
      </div>
      <div className="text-xl md:text-2xl font-bold text-[#0F2A4A]">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

function ClickableKPI({ icon: Icon, label, value, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left bg-white border border-stone-200 rounded-xl p-4 md:p-5 border-l-4 hover:shadow-md transition cursor-pointer"
      style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{label}</span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-stone-300" />
      </div>
      <div className="text-xl md:text-2xl font-bold" style={{ color: value > 0 ? color : "#0F2A4A" }}>{value}</div>
      <div className="text-[10px] text-stone-500 mt-1">Click to view</div>
    </button>
  );
}

function DrillModal({ kind, onClose, overdueItems, pendingDocs, activeProjectId, onUpload }) {
  const isOverdue = kind === "overdue";
  const title = isOverdue ? "Overdue Compliance Items" : "Pending Documents";
  const items = isOverdue ? overdueItems : pendingDocs;

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`drill-${kind}`}>
        <div className="bg-[#0F2A4A] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#72BE44] font-bold">{isOverdue ? "Compliance" : "Documents"}</div>
            <h3 className="text-lg font-bold mt-0.5">{title} · {items.length}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-center py-10 text-stone-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#72BE44]" />
              All caught up — nothing here!
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(it => isOverdue ? (
                <div key={it.id} className="flex items-center justify-between border border-rose-200 bg-rose-50 rounded-lg p-3">
                  <div>
                    <div className="font-semibold text-sm text-stone-900">{it.title}</div>
                    <div className="text-xs text-stone-600 mt-0.5">{it.type} · Due {it.due_date} · {it.authority || "—"}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-rose-600 text-white rounded font-bold">Overdue</span>
                </div>
              ) : (
                <div key={it.id} className="flex items-center justify-between border border-amber-200 bg-amber-50 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-amber-700" />
                    <div className="font-medium text-sm text-stone-900">{it.name}</div>
                  </div>
                  <label data-testid={`drill-upload-${it.id}`}
                    className="text-xs flex items-center gap-1 bg-[#72BE44] hover:bg-[#5da636] text-white px-3 py-1.5 rounded font-medium cursor-pointer">
                    <Upload className="w-3 h-3" /> Upload
                    <input type="file" hidden onChange={(e) => onUpload(it.id, e.target.files[0], activeProjectId)} />
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
